import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function GroupMasterTable({
  records = [],
  isLoading = false,
  onArchive,
  onDelete,
  onResetFilters,
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Employee Groups...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">group</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No entries meet selected criteria</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            Either there are no entries in the selected module or you have selected invalid filter conditions.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Name</th>
              <th className="px-5 py-3.5">Weekly Off</th>
              <th className="px-5 py-3.5">Second Weekly Off</th>
              <th className="px-5 py-3.5">Status</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => (
              <tr
                key={item.id}
                className="transition-colors hover:bg-surface-container-low/50 group cursor-pointer"
                onClick={() => navigate(`/payroll/employee-group/entry?id=${item.id}`)}
              >
                {/* Name */}
                <td className="px-5 py-3.5 font-bold text-on-surface group-hover:text-primary transition-colors">
                  {item.name}
                </td>

                {/* Weekly Off */}
                <td className="px-5 py-3.5">
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-0.5 text-xs font-bold text-primary border border-primary/20">
                    {item.weeklyOff}
                  </span>
                </td>

                {/* Second Weekly Off */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface-variant">
                  {item.secondWeeklyOff || 'None'}
                </td>

                {/* Status */}
                <td className="px-5 py-3.5">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                      item.status === 'Active'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                        : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        item.status === 'Active' ? 'bg-emerald-600' : 'bg-amber-600'
                      }`}
                    />
                    {item.status}
                  </span>
                </td>

                {/* Actions */}
                <td
                  className="px-5 py-3.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      {
                        key: 'archive',
                        label: item.status === 'Active' ? 'Archive' : 'Activate',
                        icon: item.status === 'Active' ? 'archive' : 'unarchive',
                      },
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') navigate(`/payroll/employee-group/entry?id=${item.id}`);
                      if (key === 'archive') onArchive?.(item);
                      if (key === 'delete') onDelete?.(item.id, item.name);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
